import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EstadoPostventaChip from "./EstadoPostventaChip";
import { colors, formatDate, formatMoney, paymentLabels, paymentOptionLabels } from "./postventaUtils";

const formatPayment = (row) => {
  const formas = row.venta?.formas_pago;
  if (Array.isArray(formas) && formas.length) {
    return formas.map((key) => paymentOptionLabels[key] || key).join(" + ");
  }
  return paymentLabels[row.forma_pago] || row.forma_pago || "-";
};

const getPaymentLines = (row) => {
  const formas = row.venta?.formas_pago;
  if (!Array.isArray(formas) || formas.length < 3) return [formatPayment(row)];
  const labels = formas.map((key) => paymentOptionLabels[key] || key);
  return [labels.slice(0, 2).join(" + "), labels.slice(2).join(" + ")].filter(Boolean);
};

export default function PostventaTable({
  rows = [],
  loading,
  total,
  page,
  rowsPerPage,
  subtitle,
  updatingId,
  onPageChange,
  onOpenFicha,
  onToggleEstado,
}) {
  return (
    <Paper elevation={0} sx={{ borderRadius: "10px", border: `1px solid ${colors.line}`, overflow: "hidden", boxShadow: "0 12px 28px rgba(19,39,84,0.08)" }}>
      <TableContainer sx={{ height: "46vh" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ "& th": { color: colors.muted, fontSize: 11, fontWeight: 900, borderColor: colors.line, py: 1.5 } }}>
              <TableCell>CLIENTE</TableCell>
              <TableCell>VENDEDOR</TableCell>
              <TableCell>VEHÍCULO</TableCell>
              <TableCell>FECHA</TableCell>
              <TableCell>TIPO</TableCell>
              <TableCell>FICHA</TableCell>
              <TableCell>ESTADO</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 8, color: colors.muted }}>
                  No hay ventas para los filtros seleccionados.
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.map((row) => (
              <TableRow key={row.id} hover sx={{ "& td": { borderColor: colors.line, py: 1.4, color: colors.ink, fontSize: 13 } }}>
                <TableCell>
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <Box sx={{ width: 30, height: 30, borderRadius: "50%", bgcolor: "rgba(0,89,246,0.08)", display: "grid", placeItems: "center", color: colors.blue }}>
                      <PersonOutlineIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{row.cliente_nombre}</Typography>
                      <Typography sx={{ fontSize: 11, color: colors.muted }}>{row.cliente_telefono || "Sin teléfono"}</Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>{row.vendedor_nombre || "Sin vendedor"}</TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{row.vehiculo_consultado || `${row.venta?.marca || ""} ${row.venta?.modelo || ""}`.trim() || "-"}</Typography>
                  <Typography sx={{ fontSize: 11, color: colors.muted }}>{formatMoney(row.venta?.precio_venta, row.venta?.moneda_venta)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                    {formatDate(row.fecha_entrega || row.fecha_venta)}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: colors.muted, fontWeight: 400 }}>
                    {row.fecha_entrega ? "Entregado" : "Venta registrada"}
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 190 }}>
                  {getPaymentLines(row).map((line, index) => (
                    <Typography key={`${row.id}-payment-${index}`} sx={{ fontSize: 13, lineHeight: 1.35, color: colors.ink }}>
                      {line}
                    </Typography>
                  ))}
                </TableCell>
                <TableCell>
                  <Button variant="outlined" size="small" startIcon={<VisibilityOutlinedIcon />} onClick={() => onOpenFicha?.(row)} sx={{ borderRadius: "8px", fontWeight: 800, textTransform: "none" }}>
                    Ver ficha
                  </Button>
                </TableCell>
                <TableCell>
                  <EstadoPostventaChip value={row.estado_postventa} disabled={updatingId === row.id} onToggle={() => onToggleEstado?.(row)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, borderTop: `1px solid ${colors.line}` }}>
        <Typography sx={{ fontSize: 12, color: colors.muted }}>{subtitle}</Typography>
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          rowsPerPageOptions={[]}
          labelRowsPerPage=""
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Stack>
    </Paper>
  );
}
